import React, { useState } from 'react';
import Icon from '../Icon';
import { C } from '../../utils/styles';
import SmartCensusUpload from './SmartCensusUpload';
import { useConfig } from '../../context/ConfigContext';
import { buildClientSummaryFromEmployees } from '../../utils/buildClientSummary';
import api from '../../utils/api';

// CensusUploadStep is the Onboarding employer-portal step that handles census
// data. It delegates the actual upload UX to SmartCensusUpload (multi-format
// detection, transformation, validation), while keeping the post-upload
// "Census Accepted" summary card and the "Re-upload" / "Continue" controls.

export default function CensusUploadStep({ client, onComplete }) {
  // When the user clicks any "Re-upload" button on the CensusUploaded view,
  // we flip this so the SmartCensusUpload UI is shown again even though the
  // client still has the previously-uploaded employees on record.
  const [forceUploadView, setForceUploadView] = useState(false);
  const config = useConfig();

  const hasCensus = client?.employees && client.employees.length > 0;

  const exitReuploadMode = () => setForceUploadView(false);

  if (hasCensus && !forceUploadView) {
    return (
      <CensusUploaded
        client={client}
        onReUpload={() => setForceUploadView(true)}
        onComplete={onComplete}
      />
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        STEP 3: Census &amp; Payroll Integration
      </h2>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
        Upload your employee census and connect your payroll system.
      </p>

      {forceUploadView && hasCensus && (
        <div
          data-testid="census-reupload-banner"
          style={{
            background: C.blueLight,
            border: `1px solid ${C.blue}`,
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: 13,
          }}
        >
          <div>
            <strong>Re-uploading census.</strong> Your current census ({client.employees.length} employees) will stay until you accept the new upload.
          </div>
          <button
            onClick={exitReuploadMode}
            data-testid="census-reupload-cancel"
            style={{
              padding: '6px 12px',
              background: '#fff',
              color: C.blue,
              border: `1px solid ${C.blue}`,
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Cancel re-upload
          </button>
        </div>
      )}

      {/* Contact info — preserved from the prior layout */}
      <div
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>
          Your contact for this step:
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
          Cassie Nix · Director of Operations/Payroll
        </div>
        <div style={{ fontSize: 13, color: C.text }}>
          c.nix@healthcues.com · 678.522.9988
        </div>
      </div>

      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
        3a. Upload Your Employee Census
      </h3>

      <SmartCensusUpload
        client={client}
        onUploaded={async ({ employees: parsed }) => {
          // SmartCensusUpload already POSTed employees to /api/clients/{id}/census/upload.
          // Backend stores employees but does NOT recompute the rollup
          // (eligible / FICA / benefit / hcRevenue). Without that the
          // Clients dropdown, Clients table and Update-Client modal would
          // show "0 eligible of N total". So we recompute summary client-side
          // and PATCH it onto the client record before the page reload.
          try {
            const newSummary = buildClientSummaryFromEmployees(parsed, config);
            if (client?.id) {
              await api.clients.update(client.id, { ...client, employees: parsed, summary: newSummary });
            }
          } catch (err) {
            // Non-fatal — backend already has employees; reload will surface
            // them. Logging keeps the failure visible during development.
            // eslint-disable-next-line no-console
            console.warn('[CensusUploadStep] summary mirror failed:', err);
          }
          window.location.reload();
        }}
        onCancel={hasCensus ? exitReuploadMode : null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CensusUploaded — the post-acceptance summary card. Reads both `salary` and
// `annualSalary` (parseCSV / SmartCensusUpload always write both for forward
// compatibility, but legacy admin uploads sometimes only had `annualSalary`).
// ---------------------------------------------------------------------------

function CensusUploaded({ client, onReUpload, onComplete }) {
  const employees = client.employees || [];
  const salaryOf = (emp) => Number(emp?.salary) || Number(emp?.annualSalary) || 0;
  const totalSalary = employees.reduce((sum, emp) => sum + salaryOf(emp), 0);
  const avgSalary = employees.length > 0 ? totalSalary / employees.length : 0;

  const states = {};
  employees.forEach((emp) => {
    const state = emp.state || 'Unknown';
    states[state] = (states[state] || 0) + 1;
  });
  const statesText = Object.entries(states)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([state, count]) => `${state} (${count})`)
    .join(', ');

  const inferredCount = employees.filter((e) => e.filingStatusInferred).length;
  const employeesWithoutEmail = employees.filter((emp) => !emp.email || String(emp.email).trim() === '');

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        3a. Employee Census
      </h2>

      <div
        style={{
          background: C.greenLight,
          border: `1px solid ${C.green}`,
          borderRadius: 12,
          padding: 32,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'start', gap: 16, marginBottom: 24 }}>
          <Icon name="checkmark-circle" style={{ fontSize: 48, color: C.green }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              Census Accepted — {employees.length} Employees
            </h3>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 0 }}>
              Your employee data has been submitted and is being processed. HealthCues will analyze your workforce and determine the optimal plan for each employee.
            </p>
          </div>
        </div>

        {employeesWithoutEmail.length > 0 && (
          <div
            data-testid="census-email-warning"
            style={{
              background: 'rgb(254, 243, 199)',
              border: '1px solid rgb(253, 230, 138)',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
              display: 'flex',
              alignItems: 'start',
              gap: 12,
            }}
          >
            <Icon name="warning-outline" style={{ fontSize: 20, color: 'rgb(217, 119, 6)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'rgb(120, 53, 15)', marginBottom: 6 }}>
                Missing Email Addresses ({employeesWithoutEmail.length} employee{employeesWithoutEmail.length !== 1 ? 's' : ''})
              </div>
              <div style={{ fontSize: 13, color: 'rgb(120, 53, 15)', lineHeight: 1.5, marginBottom: 8 }}>
                Some employees are missing email addresses. They cannot be invited to the enrollment portal until email addresses are added. You can add them inline on Step 4 (Enrollment) or re-upload an updated census.
              </div>
              <button
                onClick={onReUpload}
                data-testid="census-reupload-with-emails"
                style={{
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#fff',
                  background: 'rgb(217, 119, 6)',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { e.target.style.background = 'rgb(180, 83, 9)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgb(217, 119, 6)'; }}
              >
                Re-upload with Email Addresses
              </button>
            </div>
          </div>
        )}

        {inferredCount > 0 && (
          <div
            data-testid="census-inferred-warning"
            style={{
              background: '#e0f2fe',
              border: '1px solid #7dd3fc',
              borderRadius: 8,
              padding: 12,
              marginBottom: 24,
              display: 'flex',
              alignItems: 'start',
              gap: 12,
              fontSize: 13,
              color: '#075985',
            }}
          >
            <Icon name="information-circle-outline" style={{ fontSize: 18, color: '#0284c7', flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>{inferredCount} employees</strong> have inferred filing status (assigned via realistic distribution because the source file didn't include it). Aggregate eligibility numbers are accurate; individual employees can have their real status added later — it will overwrite the inferred value.
            </div>
          </div>
        )}

        <div
          style={{
            background: 'white',
            borderRadius: 8,
            padding: 20,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Employees:</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{employees.length}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Avg Salary:</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>${Math.round(avgSalary).toLocaleString()}</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>States:</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{statesText}</div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: C.bg,
            borderRadius: 8,
            padding: 16,
            marginBottom: 24,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>What happens next:</div>
          <div style={{ color: C.textMid }}>
            Your HealthCues team will review the census, run the optimization analysis, and share the results with you. You'll receive an email when the analysis is ready.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onReUpload}
            data-testid="census-reupload-updated"
            style={{
              padding: '10px 20px',
              background: 'white',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="cloud-upload-outline" style={{ fontSize: 16 }} />
            Upload Updated Census
          </button>

          <button
            onClick={onComplete}
            data-testid="census-continue-to-payroll"
            style={{
              padding: '10px 20px',
              background: C.blue,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Continue to Payroll Setup
            <Icon name="arrow-forward" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>
    </div>
  );
}
